import client from "shared/utils/axios-config";
import {
  PayrollSetting,
  UpdatePayrollSettingsForm,
  PayrollClassSummary,
  ClassCommissionDetail,
  UpsertClassSettingForm,
  DuplicateClassSettingForm,
  UpsertClassTiersForm,
  PayrollClassTier,
  UpsertPayrollRateForm,
  PayrollRecurringItem,
  RecurringItemForm,
  PayrollStaffOption,
  GenerateRunForm,
  PayrollRun,
  PayrollRunListItem,
  UpdateLineSalaryForm,
  UpdateStaffBaseSalaryForm,
  DraftApplyChoice,
  AddLineItemForm,
  UpdateLineItemForm,
  Payslip,
} from "shared/types";

export const getPayrollSettings = async (): Promise<PayrollSetting> => {
  const res = await client.get("/v1/payroll/settings");
  return res.data.data;
};
export const updatePayrollSettings = async (form: UpdatePayrollSettingsForm) => {
  const res = await client.put("/v1/payroll/settings", form);
  return res.data;
};
export const cancelPendingPayrollSettings = async () => {
  const res = await client.delete("/v1/payroll/settings/pending");
  return res.data;
};

export const getPayrollClasses = async (): Promise<PayrollClassSummary[]> => {
  const res = await client.get("/v1/payroll/classes");
  return res.data.data;
};
export const getClassCommissionDetail = async (classTypeId: string): Promise<ClassCommissionDetail> => {
  const res = await client.get(`/v1/payroll/class-settings/${classTypeId}`);
  return res.data.data;
};
export const upsertClassCommissionSetting = async ({
  classTypeId,
  ...form
}: UpsertClassSettingForm & { classTypeId: string }) => {
  const res = await client.put(`/v1/payroll/class-settings/${classTypeId}`, form);
  return res.data;
};
// Typed (deviates from the strict "create=untyped" rule) since the caller
// needs the fresh tier ids to re-point instructor tier_amounts at, the same
// way generatePayrollRun's return type is used above.
export const upsertClassCommissionTiers = async ({
  classTypeId,
  ...form
}: UpsertClassTiersForm & { classTypeId: string }): Promise<PayrollClassTier[]> => {
  const res = await client.put(`/v1/payroll/class-settings/${classTypeId}/tiers`, form);
  return res.data.data;
};
export const duplicateClassCommissionSetting = async ({
  classTypeId,
  ...form
}: DuplicateClassSettingForm & { classTypeId: string }) => {
  const res = await client.post(`/v1/payroll/class-settings/${classTypeId}/duplicate`, form);
  return res.data;
};

export const upsertPayrollRate = async (form: UpsertPayrollRateForm) => {
  const res = await client.put("/v1/payroll/rates", form);
  return res.data;
};
export const deletePayrollRate = async (id: string) => {
  const res = await client.delete(`/v1/payroll/rates/${id}`);
  return res.data;
};

export const getPayrollRecurringItems = async (): Promise<PayrollRecurringItem[]> => {
  const res = await client.get("/v1/payroll/recurring-items");
  return res.data.data;
};
export const createPayrollRecurringItem = async (form: RecurringItemForm) => {
  const res = await client.post("/v1/payroll/recurring-items", form);
  return res.data;
};
export const updatePayrollRecurringItem = async ({ id, ...form }: RecurringItemForm & { id: string }) => {
  const res = await client.put(`/v1/payroll/recurring-items/${id}`, form);
  return res.data;
};
export const deletePayrollRecurringItem = async (id: string, apply_to?: DraftApplyChoice) => {
  const res = await client.delete(`/v1/payroll/recurring-items/${id}`, apply_to ? { params: { apply_to } } : undefined);
  return res.data;
};

export const getPayrollStaff = async (includeDeparted: boolean): Promise<PayrollStaffOption[]> => {
  const res = await client.get("/v1/payroll/staff", { params: { include_departed: includeDeparted } });
  return res.data.data;
};
export const updateStaffBaseSalary = async ({ staffId, amount, apply_to }: UpdateStaffBaseSalaryForm) => {
  const res = await client.put(`/v1/payroll/staff/${staffId}/base-salary`, apply_to ? { amount, apply_to } : { amount });
  return res.data;
};

// Typed (deviates from the strict "create=untyped" rule) since the caller
// navigates to the new run by id on success.
export const generatePayrollRun = async (form: GenerateRunForm): Promise<PayrollRun> => {
  const res = await client.post("/v1/payroll/runs", form);
  return res.data.data;
};
export const getPayrollRuns = async (): Promise<PayrollRunListItem[]> => {
  const res = await client.get("/v1/payroll/runs");
  return res.data.data;
};
export const getPayrollRun = async (runId: string): Promise<PayrollRun> => {
  const res = await client.get(`/v1/payroll/runs/${runId}`);
  return res.data.data;
};

export const updatePayrollLineSalary = async ({ runId, lineId, amount }: UpdateLineSalaryForm) => {
  const res = await client.put(`/v1/payroll/runs/${runId}/lines/${lineId}/salary`, { amount });
  return res.data;
};
export const addPayrollLineItem = async ({ runId, lineId, ...form }: AddLineItemForm) => {
  const res = await client.post(`/v1/payroll/runs/${runId}/lines/${lineId}/items`, form);
  return res.data;
};
export const updatePayrollLineItem = async ({ runId, lineId, itemId, ...form }: UpdateLineItemForm) => {
  const res = await client.put(`/v1/payroll/runs/${runId}/lines/${lineId}/items/${itemId}`, form);
  return res.data;
};
export const deletePayrollLineItem = async ({
  runId,
  lineId,
  itemId,
}: {
  runId: string;
  lineId: string;
  itemId: string;
}) => {
  const res = await client.delete(`/v1/payroll/runs/${runId}/lines/${lineId}/items/${itemId}`);
  return res.data;
};

export const deletePayrollRun = async (runId: string) => {
  const res = await client.delete(`/v1/payroll/runs/${runId}`);
  return res.data;
};
export const finalizePayrollRun = async (runId: string) => {
  const res = await client.post(`/v1/payroll/runs/${runId}/finalize`);
  return res.data;
};
export const reopenPayrollRun = async (runId: string) => {
  const res = await client.post(`/v1/payroll/runs/${runId}/reopen`);
  return res.data;
};
export const recomputePayrollRun = async (runId: string) => {
  const res = await client.post(`/v1/payroll/runs/${runId}/recompute`);
  return res.data;
};

export const getPayrollPayslip = async ({ runId, lineId }: { runId: string; lineId: string }): Promise<Payslip> => {
  const res = await client.get(`/v1/payroll/runs/${runId}/lines/${lineId}/payslip`);
  return res.data.data;
};
export const getPayrollRunPayslips = async (runId: string): Promise<Payslip[]> => {
  const res = await client.get(`/v1/payroll/runs/${runId}/payslips`);
  return res.data.data;
};
